import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchAllFarmTypes,
  getCalendarMonth,
  getCalendarYears,
} from "../../slices/serviceagreements.slice";
import { Option } from "../../screens/ServiceAgreements/types";
import { RootState } from "../../store";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { MultiSelectDropdown } from "../MultiSelect/MultiSelectDropdownComponent";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import CustomButton from "../Button";
import React from "react";
import {
  deleteInvoice,
  getBillingEntity,
  getFieldTypesonLandlord,
  getInvoicesGrid,
} from "../../slices/billingInvoices.slice";
import styles from "../../screens/ServiceAgreements/ServiceAgreements.module.scss";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ViewBillModal from "../../screens/Modals/action-bill-modal/ViewBill";
import InvoiceGrid from "../../screens/Modals/action-bill-modal/InvoiceGrid";
import AddBillComponent from "../../screens/Modals/add-bill-modal/AddNewInvoice";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";

type FilterParams = {
  calendarYear: number;
  calendarMonth: number[];
  landlordId: number[];
  partnerId: number[];
  billTitle: string;
  billTypes: string[];
};

const LandlordShareBillComponent: React.FC = () => {
  const [selectedfarmTypes, setSelectedFarmTypes] = useState<Option[]>([]);
  const [editModal, setEditModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedBillTypes, setSelectedBillTypes] = useState<Option[]>([]);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [htmlString, setHtmlString] = useState("");
  const [loading, setLoading] = useState(false);

  const initialFilter: FilterParams = {
    calendarYear: currentYear,
    calendarMonth: [],
    landlordId: [],
    partnerId: [],
    billTitle: "",
    billTypes: [],
  };

  const [selectedMonth, setSelectedMonth] = useState<Option[]>([]);
  const dispatch = useAppDispatch();
  const customTitleRef = useRef("");
  const [selectedBillingEntity, setSelectedBillingEntity] = useState<Option[]>(
    []
  );
  const [addModal, setAddModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { farmTypes, calendarMonths, calendarYears } = useAppSelector(
    (state: RootState) => state.serviceagreements
  );
  const { invoicesList, billingEntity } = useAppSelector(
    (state: RootState) => state.billingEntity
  );
  const billTypes: Option[] = useMemo(
    () => [
      { id: 1, name: "Manual" },
      { id: 2, name: "System" },
    ],
    []
  );

  const [previousSelectedFarmType, setpreviousSelectedFarmType] =
    useState(farmTypes);
  const [initialBillingEntity, setInitialBillingEntity] =
    useState(billingEntity);
  const [previousSelectedMonth, setPreviousSelectedMonth] =
    useState(calendarMonths);

  const paginationModel = { pageSize: 20 };

  const [filterParams, setFilterParams] = useState<FilterParams>(initialFilter);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLandlordId, setSelectedLandlordId] = useState<number>();

  const columns: GridColDef[] = [
    {
      field: "calendarYear",
      headerName: "Calendar Year",
      width: 200,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "calendarMonth",
      headerName: "Calendar Month",
      width: 200,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "landlord",
      headerName: "Landlord",
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      hideable: true,
    },
    {
      field: "billingEntity",
      headerName: "Billing Entity",
      width: 200,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "billTitle",
      headerName: "Bill Title",
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "totalDue",
      headerName: "Bill Amount",
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "billType",
      headerName: "Bill Type",
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      align: "center",
      width: 120,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <GridActionsCellItem
            icon={<VisibilityIcon fontSize="medium" />}
            disabled={!row.invoiceHtml}
            label="View Bill"
            className="textPrimary"
            onClick={() => {
              setSelectedInvoiceId(row.id);
              setHtmlString(row.invoiceHtml);
              setModalOpen(true);
            }}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<ModeEditIcon fontSize="medium" />}
            label="Edit Invoice"
            className="textPrimary"
            onClick={() => {
              setSelectedInvoiceId(row.id);
              setSelectedLandlordId(row.landlordId);
              setEditModal(true);
            }}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteForeverRoundedIcon fontSize="medium" />}
            label="Delete Invoice"
            className="textPrimary"
            onClick={() => {
              setSelectedInvoiceId(row.id);
              setShowDeleteDialog(true);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    dispatch(getCalendarYears());
    dispatch(fetchAllFarmTypes());
    dispatch(getCalendarMonth(selectedYear));
    const landlordIds = selectedfarmTypes.map((id) => id.id);
    dispatch(getBillingEntity({ id: landlordIds }));
  }, []);

  useEffect(() => {
    setLoading(false);
  }, [invoicesList]);

  useEffect(() => {
    setSelectedBillTypes(billTypes);
  }, []);

  useEffect(() => {
    if (calendarMonths.length > 0) {
      setSelectedMonth(calendarMonths);
      setPreviousSelectedMonth(calendarMonths);
    }
  }, [calendarMonths]);

  useEffect(() => {
    if (calendarYears.length > 0) {
      setSelectedYear(currentYear);
    }
  }, [calendarYears]);

  useEffect(() => {
    if (farmTypes.length > 0) {
      setSelectedFarmTypes(farmTypes);
      setpreviousSelectedFarmType(farmTypes);
    }
  }, [farmTypes]);

  useEffect(() => {
    setSelectedBillingEntity(billingEntity);
    setInitialBillingEntity(billingEntity);
  }, [billingEntity]);

  useEffect(() => {
    if (filterParams) {
      setLoading(true);
      dispatch(getInvoicesGrid(filterParams));
    }
  }, [filterParams]);

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarExport>
          slotProps=
          {{
            tooltip: { title: "Export data" },
            button: { variant: "outlined" },
          }}
        </GridToolbarExport>
      </GridToolbarContainer>
    );
  }
  function handleYearChange(event: any): void {
    setSelectedYear(event.target.value);
    dispatch(getCalendarMonth(event.target.value));
  }

  function onChangeLandlordName(selectedOptions: Option[]): void {
    setSelectedFarmTypes(selectedOptions);
    const landlordIds: number[] = selectedOptions.map((id) => id.id);
    dispatch(getBillingEntity({ id: landlordIds }));
  }

  function filterRecords() {
    const billingEntityIds = selectedBillingEntity.map((id) => id.id);
    const landlordIds = selectedfarmTypes.map((id) => id.id);

    setFilterParams({
      calendarYear: selectedYear,
      calendarMonth: selectedMonth.map((id) => id.id),
      landlordId: landlordIds,
      partnerId: billingEntityIds,
      billTitle: searchTitle,
      billTypes: selectedBillTypes.map((id) => id.name),
    });
  }

  const getDisabledState = () => {
    if (
      selectedYear !== currentYear ||
      selectedfarmTypes !== previousSelectedFarmType ||
      initialBillingEntity !== selectedBillingEntity ||
      previousSelectedMonth !== selectedMonth ||
      selectedBillTypes !== billTypes ||
      !!searchTitle
    ) {
      return false;
    } else {
      return true;
    }
  };

  const setEditModalClose = (isReloadRequired: boolean) => {
    setEditModal(false);
    setSelectedInvoiceId("");
    if (isReloadRequired) {
      dispatch(getInvoicesGrid(filterParams));
    }
  };

  const setAddModalClose = (isReloadRequired: boolean) => {
    setAddModal(false);
    setSelectedInvoiceId("");
    if (isReloadRequired) {
      resetFilters();
    }
  };

  function resetFilters() {
    const landlordIds = farmTypes.map((id) => id.id);
    dispatch(getFieldTypesonLandlord({ id: landlordIds }));
    dispatch(getBillingEntity({ id: landlordIds }));
    dispatch(getCalendarMonth(currentYear));
    setSelectedBillingEntity(billingEntity);
    setSelectedFarmTypes(farmTypes);
    setSelectedMonth(calendarMonths);
    setSelectedYear(currentYear);
    setSelectedBillTypes(billTypes);
    setSearchTitle("");

    setFilterParams(initialFilter);
  }

  const handleDeleteYes = () => {
    dispatch(deleteInvoice(selectedInvoiceId)).then(() => {
      setAddModalClose(true);
    });
    setShowDeleteDialog(false);
  };

  return (
    <div>
      <div className={styles.containerView}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "5px",
          }}
        >
          <Typography variant="h5" color="primary" sx={{ flex: 1 }}>
            Landlord Share Bills
          </Typography>
          <Box display={"flex"} gap={2}>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={() => {
                setAddModal(true);
              }}
            >
              Add Bill
            </CustomButton>
            <FormControl>
              <InputLabel>Year</InputLabel>
              <Select
                labelId="demo-simple-select-filled-label"
                id="demo-simple-select-filled"
                value={selectedYear}
                label="Year"
                onChange={handleYearChange}
                size="small"
              >
                {calendarYears.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MultiSelectDropdown
              options={calendarMonths}
              label="Month"
              selected={selectedMonth}
              setSelected={setSelectedMonth}
            />

            <MultiSelectDropdown
              options={farmTypes}
              label="Landlord"
              selected={selectedfarmTypes}
              setSelected={onChangeLandlordName}
            />
            <MultiSelectDropdown
              options={billingEntity}
              label="Billing Entity"
              selected={selectedBillingEntity}
              setSelected={setSelectedBillingEntity}
            />
            <MultiSelectDropdown
              options={billTypes}
              label="Bill Type"
              selected={selectedBillTypes}
              setSelected={setSelectedBillTypes}
            />
            <TextField
              label="Search Title"
              defaultValue={customTitleRef.current}
              size="small"
              onChange={(e: any) => setSearchTitle(e.target.value)}
              value={searchTitle}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Box>
          <CustomButton onClick={filterRecords} sx={{ marginLeft: 2 }}>
            Search
          </CustomButton>
          <CustomButton
            onClick={resetFilters}
            sx={{ marginLeft: 2 }}
            disabled={getDisabledState()}
          >
            Reset Filter
          </CustomButton>
        </div>
        {editModal && (
          <InvoiceGrid
            invoiceId={selectedInvoiceId}
            open={editModal}
            onClose={setEditModalClose}
            landlordId={selectedLandlordId}
          />
        )}
        <ViewBillModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="View Bill"
          htmlString={htmlString}
          invoiceId={selectedInvoiceId}
        />
        {addModal && (
          <AddBillComponent
            isOpen={addModal}
            onClose={setAddModalClose}
            title="Add New Bill"
          />
        )}
        <DataGrid
          loading={loading}
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: "primary.light",
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
          }}
          rows={invoicesList}
          rowSelection={true}
          columns={columns}
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[10, 20, 30]}
          slots={{ toolbar: CustomToolbar }}
        />
        <ConfirmationDialog
          open={showDeleteDialog}
          title="Delete"
          description="Are you sure you want to delete this item? This action cannot be undone."
          handleNo={() => setShowDeleteDialog(false)}
          handleYes={handleDeleteYes}
        />
      </div>
    </div>
  );
};

export default LandlordShareBillComponent;
